import type { Event } from "@sentry/react";
import * as Sentry from "@sentry/react";
import { ApiError, isApiFetchError, isErrorResponse } from "../api";

// The plain-object form of CaptureContext, i.e. Partial<ScopeContext>, which
// @sentry/react does not export directly.
type Hint = Exclude<
    Sentry.CaptureContext,
    Sentry.Scope | ((scope: Sentry.Scope) => Sentry.Scope)
>;

export const captureException = (err: unknown, hint?: Hint) => {
    if (isApiFetchError(err)) {
        const exception = new ApiFetchErrorException(err);
        return Sentry.captureException(exception, {
            ...hint,
            extra: {
                ...hint?.extra,
                status: err.response?.status,
                statusText: err.response?.statusText,
                method: err.method,
                url: stripQueryString(err.url),
                requestId:
                    err.response?.headers?.get("request-id") ?? undefined,
                errorCode: isErrorResponse(err.data)
                    ? err.data.error.code
                    : undefined,
                errorMessage: isErrorResponse(err.data)
                    ? err.data.error.message
                    : undefined,
                data: redactSensitiveFields(err.data),
            },
        });
    }

    return Sentry.captureException(err, hint);
};

class ApiFetchErrorException extends Error {
    constructor(error: ApiError) {
        super(buildExceptionMessage(error));
        this.name = "ApiFetchErrorException";
        if (error instanceof Error) {
            this.stack = error.stack;
        }
    }
}

const buildExceptionMessage = (error: ApiError): string => {
    const status = error.response?.status ?? "unknown";
    const code = isErrorResponse(error.data)
        ? error.data.error.code
        : undefined;
    const codeSuffix = code ? ` [${code}]` : "";
    return `API ${error.method} ${stripQueryString(error.url)} failed with status ${status}${codeSuffix}`;
};

const stripQueryString = (url: string): string => {
    const i = url.indexOf("?");
    return i === -1 ? url : url.slice(0, i);
};

const REDACTED = "[REDACTED]";
const CIRCULAR = "[CIRCULAR]";
const MAX_REDACTION_DEPTH = 10;

// Field names whose values may contain personally identifiable information.
// Matching is case-insensitive and ignores `_` / `-` separators, so e.g.
// `firstName`, `first_name` and `FIRST-NAME` all match `firstname`.
const SENSITIVE_FIELD_NAMES = new Set([
    "phone_number",
    "first_name",
    "last_name",
    "address_line",
    "address_line_2",
    "address_line_3",
    "postal_code",
    "postal_place",
    "name",
    "given_name",
    "family_name",
    "social_security_number",
    "national_country",
    "masked_pan",
    "recurrence_token",
    "recurrence_token_id",
    "payment_token",
    "payment_token_id",
    "expiry_date",
    "verified_identity",
    "card",
    "access_token",
    "refresh_token",
    "verification_code",
    "name",
    "firstname",
    "lastname",
    "fullname",
    "middlename",
    "cardholdername",
    "email",
    "emailaddress",
    "phone",
    "phonenumber",
    "mobile",
    "mobilenumber",
    "address",
    "addressline",
    "addressline1",
    "addressline2",
    "street",
    "streetaddress",
    "city",
    "postalplace",
    "zip",
    "zipcode",
    "postalcode",
    "birthdate",
    "dateofbirth",
    "dob",
    "ssn",
    "nationalid",
    "personalnumber",
    "taxid",
    "vatnumber",
    "passport",
    "iban",
    "bban",
    "accountnumber",
    "cardnumber",
    "pan",
    "cvv",
    "cvc",
    "pin",
    "password",
    "secret",
    "token",
    "accesstoken",
    "refreshtoken",
    "authorization",
]);

const normalizeKey = (key: string): string =>
    key.toLowerCase().replace(/[_-]/g, "");

// Generic so it can be passed directly as `beforeSend`, which receives and
// must return an ErrorEvent.
export const scrubEventExtras = <T extends Event>(event: T): T => {
    if (event.extra) {
        event.extra = redactSensitiveFields(event.extra) as T["extra"];
    }
    return event;
};

export const redactSensitiveFields = (
    value: unknown,
    depth = 0,
    seen: WeakSet<object> = new WeakSet(),
): unknown => {
    if (depth > MAX_REDACTION_DEPTH) return REDACTED;
    if (!value || typeof value !== "object") return value;
    if (seen.has(value)) return CIRCULAR;
    seen.add(value);
    if (Array.isArray(value)) {
        return value.map((v) => redactSensitiveFields(v, depth + 1, seen));
    }
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
        if (SENSITIVE_FIELD_NAMES.has(normalizeKey(key))) {
            result[key] = REDACTED;
        } else {
            result[key] = redactSensitiveFields(val, depth + 1, seen);
        }
    }
    return result;
};
